import { useEffect, PropsWithChildren } from "react";
import { useAppDispatch, useAppState } from "hooks/AppContext";
import { UserActionTypes } from "interfaces/context/appContextInterfaces";
import { getUser } from "utils/api";
import { formatLog } from "utils/logging";
import { getCarmenData } from "utils/location";
import mockUser from "mocks/mockUser.json";
import { User, useSessionStorage } from "@themuse/design-system";

const setUserCarmenData = async (setUserLocation: any) => {
    try {
        const carmenResponse = await getCarmenData();
        if (carmenResponse) {
            setUserLocation(JSON.stringify(carmenResponse));
        }
    } catch (error) {
        const msg = `Error requesting the location from Carmen at ${error?.request?.path}.`;
        const err = JSON.stringify(formatLog({ error }));
        console.warn(msg, err);
    }
};

const UserInit = (props: PropsWithChildren<{}>) => {
    const { children } = props;
    const { user } = useAppState();
    const [userLocation, setUserLocation] = useSessionStorage("user_location");
    const appDispatch = useAppDispatch();

    useEffect(() => {
        const setUser = async () => {
            try {
                let apiUser: User;
                // User only works in production and staging.
                if (!["www.themuse.com", "www.staging-themuse.com"].includes(window.location.host)) {
                    apiUser = mockUser;
                } else {
                    apiUser = await getUser();
                }

                if (apiUser?.logged_in) {
                    appDispatch({
                        type: UserActionTypes.updateUser,
                        payload: {
                            ...apiUser,
                            location: userLocation ? JSON.parse(userLocation) : null
                        }
                    });
                } else {
                    appDispatch({ type: UserActionTypes.resetUser });
                }
            } catch (error) {
                const msg = `Error requesting the user at ${error?.request?.path}.`;
                const err = JSON.stringify(formatLog({ error }));
                console.warn(msg, err);
            }
        };

        setUser().then(() => {
            setUserCarmenData(setUserLocation);
        })
            .catch((error) => {
                const msg = `Error setting the user at ${error?.request?.path}.`;
                const err = JSON.stringify(formatLog({ error }));
                console.error(msg, err);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        appDispatch({
            type: UserActionTypes.updateUser,
            payload: {
                ...user,
                location: userLocation ? JSON.parse(userLocation) : null
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userLocation]);

    return <>{children}</>;
};
UserInit.displayName = "UserInit";

export { UserInit };
